import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import PageLayout from "@/components/layout/PageLayout";
import PageBreadcrumb from "@/components/layout/PageBreadcrumb";
import CompactQuoteButton from "@/components/CompactQuoteButton";

const generateCityContent = async (cityName: string) => {
  const { data: existingPage } = await supabase
    .from('city_pages')
    .select('*')
    .eq('city_name', cityName)
    .maybeSingle();

  // Force regeneration if content doesn't exist or is stale
  if (!existingPage || !existingPage.content || 
      (new Date().getTime() - new Date(existingPage.last_generated || 0).getTime()) > 24 * 60 * 60 * 1000) {
    const { data, error } = await supabase.functions.invoke('generate-city-content', {
      body: { cityName }
    });

    if (error) throw error;
    return data;
  }

  return existingPage;
};

const CityPage = () => {
  const { name } = useParams();
  const { toast } = useToast();
  
  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['city', name],
    queryFn: () => generateCityContent(name || ''),
    enabled: !!name,
    staleTime: 0,
    gcTime: 0,
  });

  if (error) {
    toast({
      title: "Error",
      description: "Failed to load city information. Please try again later.",
      variant: "destructive",
    });
  }

  return (
    <PageLayout>
      <div className="container mx-auto pb-16"> {/* Added pb-16 for bottom padding */}
        <PageBreadcrumb currentPage={`${name}, Illinois`} />
        
        {isLoading ? (
          <div className="px-4">
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : (
          <div className="px-4 max-w-[1200px] mx-auto">
            <div className="space-y-12">
              <h1 className="text-5xl font-bold text-primary mt-8 mb-6 text-center">
                Insurance Coverage in {name}, Illinois
              </h1>
              
              <div className="flex justify-center mb-12">
                <CompactQuoteButton />
              </div>

              <div 
                className="prose max-w-none text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: pageData?.content || '' }} 
              />

              <div className="flex flex-col items-center gap-4 mt-12 mb-8">
                <p className="text-neutral">Ready to protect what matters most?</p>
                <CompactQuoteButton />
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default CityPage;